import { RotateCw, X } from 'lucide-react';
import { IconActionButton } from '@/components/molecules/IconActionButton';
import { StatusBadge } from '@/components/atoms/org-admin/StatusBadge';
import type { OrgInvitation } from '@/services/org-admin';
import { NO_PERMISSION_REASON } from './types';

export interface InvitationRowProps {
  invitation: OrgInvitation;
  roleName: string | null;
  canManage: boolean;
  isResending: boolean;
  isCancelling: boolean;
  onResend: (invitationId: string) => void;
  onCancel: (invitationId: string) => void;
}

function formatExpiry(expiresAt: string): { label: string; isPast: boolean; title: string } {
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return { label: expiresAt, isPast: false, title: expiresAt };

  const absolute = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const days = Math.round((date.getTime() - Date.now()) / 86_400_000);

  /**
   * A relative label ("in 5 days") answers the question an admin is actually asking — is this about
   * to lapse? The absolute date stays available as the tooltip, so nothing is lost. The previous
   * "Expires Mar 14, 2026" required mental arithmetic against today's date on every row.
   */
  const label =
    days < 0
      ? `${Math.abs(days)}d ago`
      : days === 0
        ? 'today'
        : days === 1
          ? 'tomorrow'
          : `in ${days}d`;

  return { label, isPast: days < 0, title: absolute };
}

function ExpiryCell({ expiresAt, status }: { expiresAt: string; status: string }) {
  const { label, isPast, title } = formatExpiry(expiresAt);
  // Only a *pending* invitation's expiry is actionable information. On an accepted or cancelled one
  // the date is trivia, so it is muted rather than shouting in a warning colour.
  const isLive = status === 'pending';
  return (
    <time
      dateTime={expiresAt}
      title={title}
      className="oa-meta tabular-nums"
      data-tone={isLive && isPast ? 'err' : undefined}
    >
      {isPast ? 'Expired ' : 'Expires '}
      {label}
    </time>
  );
}

function InvitationActions({
  invitation,
  canManage,
  isResending,
  isCancelling,
  onResend,
  onCancel,
}: InvitationRowProps) {
  // Only a pending invitation can be resent or cancelled — an accepted one has already been used and
  // a cancelled/expired one has nothing left to act on.
  if (invitation.status !== 'pending') return null;
  const busy = isResending || isCancelling;

  return (
    <>
      <IconActionButton
        label={`Resend invitation to ${invitation.email}`}
        icon={RotateCw}
        size="sm"
        isPending={isResending}
        disabled={busy || !canManage}
        disabledReason={canManage ? undefined : NO_PERMISSION_REASON}
        onClick={() => onResend(invitation.id)}
      />
      <IconActionButton
        label={`Cancel invitation to ${invitation.email}`}
        icon={X}
        tone="destructive"
        size="sm"
        isPending={isCancelling}
        disabled={busy || !canManage}
        disabledReason={canManage ? undefined : NO_PERMISSION_REASON}
        onClick={() => onCancel(invitation.id)}
      />
    </>
  );
}

/** Table variant. */
export function InvitationRow(props: InvitationRowProps) {
  const { invitation, roleName } = props;

  return (
    <tr>
      <td>
        <span className="block truncate font-medium">{invitation.email}</span>
      </td>
      <td>
        <StatusBadge status={invitation.status} />
      </td>
      <td>
        <span className="oa-meta block truncate" data-tone="strong">
          {roleName ?? '—'}
        </span>
      </td>
      <td>
        <ExpiryCell expiresAt={invitation.expires_at} status={invitation.status} />
      </td>
      <td>
        <div className="oa-row-actions flex justify-end gap-0.5">
          <InvitationActions {...props} />
        </div>
      </td>
    </tr>
  );
}

/** Card variant, for a panel too narrow for five columns. */
export function InvitationCard(props: InvitationRowProps) {
  const { invitation, roleName } = props;

  return (
    <div className="oa-card-row">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="block truncate font-medium">{invitation.email}</span>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <StatusBadge status={invitation.status} />
            {roleName && <span className="oa-meta">as {roleName}</span>}
            <ExpiryCell expiresAt={invitation.expires_at} status={invitation.status} />
          </div>
        </div>
        <div className="flex shrink-0 gap-0.5">
          <InvitationActions {...props} />
        </div>
      </div>
    </div>
  );
}
